import { useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Send, FolderKanban } from "lucide-react";
import { supplierApi, type SupplierTaskBrief, type TaskStatus } from "@/api/supplierClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "@/framework-originated/i18n";

const STATUS_VARIANT: Record<TaskStatus, "default" | "success" | "warning" | "destructive" | "muted"> = {
  staged: "muted",
  pending: "muted",
  dispatching: "warning",
  success: "success",
  failed: "destructive",
  unknown: "warning",
  abandoned: "muted",
};

function buildStatusLabel(t: (key: string) => string): Record<TaskStatus, string> {
  return {
    staged: t("status.staged"),
    pending: t("status.pending"),
    dispatching: t("status.dispatching"),
    success: t("status.success"),
    failed: t("status.failed"),
    unknown: t("status.unknown"),
    abandoned: t("status.abandoned"),
  };
}

function relativeTime(t: (key: string, opts?: Record<string, unknown>) => string, iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diff = Date.now() - then;
  if (diff < 0) return t("relativeTime.justNow");
  const s = Math.floor(diff / 1000);
  if (s < 60) return t("relativeTime.seconds", { count: s });
  const m = Math.floor(s / 60);
  if (m < 60) return t("relativeTime.minutes", { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t("relativeTime.hours", { count: h });
  return t("relativeTime.days", { count: Math.floor(h / 24) });
}

function recentRow(
  row: SupplierTaskBrief,
  keyNames: Map<number, string>,
  statusLabel: Record<TaskStatus, string>,
  tFn: (key: string, opts?: Record<string, unknown>) => string,
) {
  return (
    <li key={row.id} className="flex items-center gap-2 text-sm">
      <Badge variant={STATUS_VARIANT[row.status]}>{statusLabel[row.status]}</Badge>
      <span className="font-medium text-foreground">{keyNames.get(row.key_id) ?? `#${row.key_id}`}</span>
      <span className="text-muted-foreground">→ {row.target_code}</span>
      <span className="ml-auto shrink-0 text-xs text-muted-foreground">{relativeTime(tFn, row.created_at)}</span>
    </li>
  );
}

// /supplier/dashboard — 我的统计 + 最近分发(全部脱敏)(doc/046 §6.1)。
export function SupplierDashboardPage() {
  const { t } = useTranslation("supplier");
  const STATUS_LABEL = buildStatusLabel(t);
  const qc = useQueryClient();
  const dashQ = useQuery({
    queryKey: ["supplier", "dashboard"],
    queryFn: supplierApi.dashboard,
  });

  const recent = dashQ.data?.recent ?? [];
  const keyIds = Array.from(new Set(recent.map((r) => r.key_id)));
  const keysQ = useQuery({
    queryKey: ["supplier", "keys", "recent", keyIds],
    queryFn: () => supplierApi.listKeys({ page: 1, page_size: Math.max(1, keyIds.length) }),
    enabled: keyIds.length > 0,
  });
  const keyNames = new Map<number, string>();
  keysQ.data?.items.forEach((k) => keyNames.set(k.id, k.name));

  const stats = dashQ.data;
  const byStatus = stats?.tasks_by_status ?? {};

  return (
    <div className="space-y-7">
      <PageHeader
        eyebrow="Supplier / Dashboard"
        title={t("dashboard.title")}
        description={t("dashboard.description")}
      />

      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-3">
        <StatCard
          label={t("dashboard.keys")}
          value={stats?.keys_count ?? "—"}
          icon={KeyRound}
          tone="blue"
        />
        <StatCard
          label={t("dashboard.targets")}
          value={stats?.targets_count ?? "—"}
          icon={Send}
          tone="green"
        />
        <StatCard
          label={t("dashboard.tasks")}
          value={stats?.tasks_total ?? "—"}
          icon={FolderKanban}
          tone="orange"
        />
      </div>

      <div className="flex flex-wrap gap-1">
        {(Object.keys(byStatus) as TaskStatus[]).map((s) => (
          <Badge key={s} variant={STATUS_VARIANT[s]}>
            {STATUS_LABEL[s]} {byStatus[s]}
          </Badge>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">{t("dashboard.recent")}</CardTitle>
        </CardHeader>
        <CardContent>
          {recent.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("dashboard.empty")}
              <Link to="/supplier/distribute" className="ml-1 text-primary underline-offset-2 hover:underline">
                {t("dashboard.goDistribute")}
              </Link>
            </p>
          ) : (
            <ul className="space-y-2">{recent.map((r) => recentRow(r, keyNames, STATUS_LABEL, t))}</ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
